import { supabase } from './supabase';
import { format, parseISO, startOfMonth, endOfMonth } from 'date-fns';

// Timezone conversion functions (same as in timeclock.ts)
function localToUTC(date: Date): string {
  return date.toISOString();
}

function utcToLocal(dateStr: string): Date {
  return new Date(dateStr);
}

interface PayrollEntry {
  id: string;
  resource_id: string;
  resource_first_name: string;
  resource_last_name: string;
  resource_email: string;
  resource_avatar_url?: string;
  compensation_type: string;
  flat_rate_amount: number | null;
  hourly_rate: number | null;
  booking_id: string | null;
  booking_number: number | null;
  booking_title: string | null;
  start_date: string | null;
  end_date: string | null;
  entry_type: 'flat_rate' | 'timeclock' | 'commission' | 'bonus';
  hours: number;
  earnings: number;
  item_name: string | null;
  item_description: string | null;
  created_at: string;
  clock_in?: string;
  clock_out?: string;
  break_start?: string;
  break_end?: string;
  total_minutes?: number;
  notes?: string;
  status?: string;
  process_status?: string;
  completed_at?: string;
  booking?: {
    id: string;
    business_id: string;
    booking_number: number;
    title: string;
    status: string;
    created_at: string;
    end_date: string;
    end_time: string;
    completed_at: string;
  };
}

export async function getPayrollEntries(businessId: string, startDate?: string, endDate?: string) {
  if (!businessId) {
    throw new Error('Business ID is required');
  }

  if (!supabase) {
    throw new Error('Supabase client not initialized');
  }

  // Convert start and end dates to UTC for database query (same as timeclock.ts)
  const localStartDate = startDate ? parseISO(startDate) : startOfMonth(new Date());
  const localEndDate = endDate ? parseISO(endDate) : endOfMonth(new Date());

  // Set the time to start and end of day in local time
  localStartDate.setHours(0, 0, 0, 0);
  localEndDate.setHours(23, 59, 59, 999);

  // Convert to UTC for database query
  const utcStartDate = localToUTC(localStartDate);
  const utcEndDate = localToUTC(localEndDate);

  console.log('Payroll date filter debug:', {
    originalStartDate: startDate,
    originalEndDate: endDate,
    localStartDate: format(localStartDate, 'yyyy-MM-dd HH:mm:ss xxx'),
    localEndDate: format(localEndDate, 'yyyy-MM-dd HH:mm:ss xxx'),
    utcStartDate,
    utcEndDate
  });

  try {
    // Build base query for flat rate entries
    let flatRateQuery = supabase
      .from('payroll_page')
      .select(`
        *,
        booking:bookings(
          id,
          business_id,
          booking_number,
          title,
          status,
          created_at,
          end_date,
          end_time,
          completed_at
        ),
        resource:resources(
          first_name,
          last_name,
          email,
          flat_rate_amount,
          hourly_rate,
          compensation_type,
          avatar_url
        ),
        item:flat_rate_items(
          name,
          description
        )
      `)
      .eq('business_id', businessId)
      .eq('entry_type', 'flat_rate');

    // Apply server-side date filtering for flat rate entries using UTC dates
    if (startDate && endDate) {
      flatRateQuery = flatRateQuery
        .gte('completed_at', utcStartDate)
        .lte('completed_at', utcEndDate);
      
      console.log('Flat rate query date filter:', {
        startDate: utcStartDate,
        endDate: utcEndDate
      });
    }

    const { data: flatRateData, error: flatRateError } = await flatRateQuery;

    if (flatRateError) {
      throw flatRateError;
    }
    
    console.log('Flat rate entries found:', flatRateData?.length || 0);
    if (flatRateData && flatRateData.length > 0) {
      console.log('Sample flat rate entries:', flatRateData.slice(0, 3).map(entry => ({
        id: entry.id,
        completed_at: entry.completed_at,
        created_at: entry.created_at
      })));
    }

    // Build base query for timeclock entries
    let timeclockQuery = supabase
      .from('payroll_page')
      .select(`
        *,
        resource:resources(
          first_name,
          last_name,
          email,
          flat_rate_amount,
          hourly_rate,
          compensation_type,
          avatar_url
        )
      `)
      .eq('business_id', businessId)
      .eq('entry_type', 'timeclock');

    // Apply server-side date filtering for timeclock entries using UTC dates
    if (startDate && endDate) {
      timeclockQuery = timeclockQuery
        .gte('clock_in', utcStartDate)
        .lte('clock_in', utcEndDate);
      
      console.log('Timeclock query date filter:', {
        startDate: utcStartDate,
        endDate: utcEndDate
      });
    }

    const { data: timeclockData, error: timeclockError } = await timeclockQuery;

    if (timeclockError) {
      throw timeclockError;
    }
    
    console.log('Timeclock entries found:', timeclockData?.length || 0);
    if (timeclockData && timeclockData.length > 0) {
      console.log('Sample timeclock entries:', timeclockData.slice(0, 3).map(entry => ({
        id: entry.id,
        clock_in: entry.clock_in,
        created_at: entry.created_at
      })));
    }

    // Build base query for commission entries
    let commissionQuery = supabase
      .from('payroll_page')
      .select(`
        *,
        booking:bookings(
          id,
          business_id,
          booking_number,
          title,
          status,
          created_at,
          end_date,
          end_time,
          completed_at
        ),
        resource:resources(
          first_name,
          last_name,
          email,
          flat_rate_amount,
          hourly_rate,
          compensation_type,
          avatar_url
        )
      `)
      .eq('business_id', businessId)
      .eq('entry_type', 'commission');

    // Apply server-side date filtering for commission entries using UTC dates
    if (startDate && endDate) {
      commissionQuery = commissionQuery
        .gte('completed_at', utcStartDate)
        .lte('completed_at', utcEndDate);
      
      console.log('Commission query date filter:', {
        startDate: utcStartDate,
        endDate: utcEndDate
      });
    }

    const { data: commissionData, error: commissionError } = await commissionQuery;
      
    if (commissionError) {
      throw commissionError;
    }
    
    console.log('Commission entries found:', commissionData?.length || 0);
    if (commissionData && commissionData.length > 0) {
      console.log('Sample commission entries:', commissionData.slice(0, 3).map(entry => ({
        id: entry.id,
        completed_at: entry.completed_at,
        created_at: entry.created_at
      })));
    }

    // Build base query for bonus entries
    let bonusQuery = supabase
      .from('payroll_page')
      .select(`
        *,
        booking:bookings(
          id,
          business_id,
          booking_number,
          title,
          status,
          created_at,
          end_date,
          end_time,
          completed_at
        ),
        resource:resources(
          first_name,
          last_name,
          email,
          flat_rate_amount,
          hourly_rate,
          compensation_type,
          avatar_url
        )
      `)
      .eq('business_id', businessId)
      .eq('entry_type', 'bonus');

    // Apply server-side date filtering for bonus entries using created_at
    if (startDate && endDate) {
      bonusQuery = bonusQuery
        .gte('created_at', utcStartDate)
        .lte('created_at', utcEndDate);
      
      console.log('Bonus query date filter:', {
        startDate: utcStartDate,
        endDate: utcEndDate
      });
    }

    const { data: bonusData, error: bonusError } = await bonusQuery;
      
    if (bonusError) {
      throw bonusError;
    }
    
    console.log('Bonus entries found:', bonusData?.length || 0);
    if (bonusData && bonusData.length > 0) {
      console.log('Sample bonus entries:', bonusData.slice(0, 3).map(entry => ({
        id: entry.id,
        created_at: entry.created_at,
        bonus_earnings: entry.bonus_earnings,
        booking_number: entry.booking?.booking_number
      })));
    }

    // Convert flat rate entries to PayrollEntry format
    const flatRateEntries = flatRateData?.map(entry => {
      // Calculate hours and earnings for flat rate entries
      const hours = entry.hours || (entry.total_minutes ? entry.total_minutes / 60 : 0);
      const rate = entry.rate_amount || entry.resource?.flat_rate_amount || 0;
      const earnings = entry.earnings || (hours * rate);
      
      const mappedEntry = {
        id: entry.id,
        resource_id: entry.resource_id,
        resource_first_name: entry.resource?.first_name,
        resource_last_name: entry.resource?.last_name,
        resource_email: entry.resource?.email,
        resource_avatar_url: entry.resource?.avatar_url,
        compensation_type: entry.resource?.compensation_type,
        flat_rate_amount: entry.resource?.flat_rate_amount,
        hourly_rate: entry.resource?.hourly_rate,
        booking_id: entry.booking_id,
        booking_number: entry.booking?.booking_number,
        booking_title: entry.booking?.title,
        start_date: entry.created_at,
        end_date: entry.completed_at || entry.created_at,
        entry_type: 'flat_rate' as const,
        hours: hours,
        earnings: earnings,
        item_name: entry.item?.name,
        item_description: entry.item?.description,
        created_at: entry.created_at,
        completed_at: entry.completed_at,
        booking: entry.booking,
        // Optional fields that might be needed for interface compatibility
        clock_in: undefined,
        clock_out: undefined,
        break_start: undefined,
        break_end: undefined,
        total_minutes: undefined,
        notes: entry.notes,
        status: entry.status,
        process_status: entry.process_status
      };

      return mappedEntry;
    }) || [];
    
    // Convert timeclock entries to PayrollEntry format
    const timeclockEntries = timeclockData?.map(entry => {
      // Calculate hours and earnings if not already set
      const hours = entry.hours || (entry.total_minutes ? entry.total_minutes / 60 : 0);
      const rate = entry.rate_amount || entry.resource?.hourly_rate || 0;
      const earnings = entry.total_earnings || (hours * rate);
      
      const mappedEntry = {
        id: entry.id,
        resource_id: entry.resource_id,
        resource_first_name: entry.resource?.first_name,
        resource_last_name: entry.resource?.last_name,
        resource_email: entry.resource?.email,
        resource_avatar_url: entry.resource?.avatar_url,
        compensation_type: entry.compensation_type || entry.resource?.compensation_type || 'hourly',
        flat_rate_amount: null,
        hourly_rate: entry.rate_amount || entry.resource?.hourly_rate,
        booking_id: null,
        booking_number: null,
        booking_title: null,
        start_date: entry.clock_in,
        end_date: entry.clock_out || entry.created_at,
        entry_type: 'timeclock' as const,
        hours: hours,
        earnings: earnings,
        item_name: null,
        item_description: null,
        created_at: entry.created_at,
        completed_at: entry.clock_out,
        // Timeclock specific fields
        clock_in: entry.clock_in,
        clock_out: entry.clock_out,
        break_start: entry.break_start,
        break_end: entry.break_end,
        total_minutes: entry.total_minutes,
        notes: entry.notes,
        status: entry.status,
        process_status: entry.process_status
      };
      
      return mappedEntry;
    }) || [];
    
    // Convert commission entries to PayrollEntry format
    const commissionEntries = commissionData?.map(entry => {
      const mappedEntry = {
        id: entry.id,
        resource_id: entry.resource_id,
        resource_first_name: entry.resource?.first_name,
        resource_last_name: entry.resource?.last_name,
        resource_email: entry.resource?.email,
        resource_avatar_url: entry.resource?.avatar_url,
        compensation_type: entry.resource?.compensation_type,
        flat_rate_amount: entry.resource?.flat_rate_amount,
        hourly_rate: entry.resource?.hourly_rate,
        booking_id: entry.booking_id,
        booking_number: entry.booking?.booking_number,
        booking_title: entry.booking?.title,
        start_date: entry.created_at,
        end_date: entry.completed_at || entry.created_at,
        entry_type: 'commission' as const,
        hours: 0, // Commissions don't have hours
        earnings: entry.total_earnings || 0,
        item_name: entry.item_name,
        item_description: entry.item_description,
        created_at: entry.created_at,
        completed_at: entry.completed_at,
        booking: entry.booking,
        // Optional fields for interface compatibility
        clock_in: undefined,
        clock_out: undefined,
        break_start: undefined,
        break_end: undefined,
        total_minutes: undefined,
        notes: entry.notes,
        status: entry.status,
        process_status: entry.process_status
      };

      return mappedEntry;
    }) || [];

    // Convert bonus entries to PayrollEntry format
    const bonusEntries = bonusData?.map(entry => {
      const mappedEntry = {
        id: entry.id,
        resource_id: entry.resource_id,
        resource_first_name: entry.resource?.first_name,
        resource_last_name: entry.resource?.last_name,
        resource_email: entry.resource?.email,
        resource_avatar_url: entry.resource?.avatar_url,
        compensation_type: entry.resource?.compensation_type,
        flat_rate_amount: entry.resource?.flat_rate_amount,
        hourly_rate: entry.resource?.hourly_rate,
        booking_id: entry.booking_id,
        booking_number: entry.booking?.booking_number,
        booking_title: entry.booking?.title,
        start_date: entry.created_at,
        end_date: entry.created_at, // Bonus entries use created_at for both
        entry_type: 'bonus' as const,
        hours: 0, // Bonuses don't have hours
        earnings: entry.bonus_earnings || entry.total_earnings || 0,
        item_name: entry.item_name || 'Booking Creation Bonus',
        item_description: entry.item_description || `Bonus for creating booking #${entry.booking?.booking_number || entry.booking_id}`,
        created_at: entry.created_at,
        completed_at: entry.created_at, // Bonus entries are created when earned
        booking: entry.booking,
        // Optional fields for interface compatibility
        clock_in: undefined,
        clock_out: undefined,
        break_start: undefined,
        break_end: undefined,
        total_minutes: undefined,
        notes: entry.notes,
        status: entry.status,
        process_status: entry.process_status || 'UNPAID'
      };

      return mappedEntry;
    }) || [];
    
    // Combine all types of entries
    const allEntries = [...flatRateEntries, ...timeclockEntries, ...commissionEntries, ...bonusEntries];
    
    console.log(`Payroll Entries: Found ${flatRateEntries.length} flat rate entries, ${timeclockEntries.length} timeclock entries, ${commissionEntries.length} commission entries, and ${bonusEntries.length} bonus entries`);

    // Sort entries by date (most recent first) - no client-side filtering needed since it's done server-side
    return allEntries.sort((a, b) => {
      const dateA = a.entry_type === 'timeclock' && a.clock_in
        ? new Date(a.clock_in)
        : a.completed_at
          ? new Date(a.completed_at)
          : new Date(a.end_date || a.created_at);
          
      const dateB = b.entry_type === 'timeclock' && b.clock_in
        ? new Date(b.clock_in)
        : b.completed_at
          ? new Date(b.completed_at)
          : new Date(b.end_date || b.created_at);
          
      return dateB.getTime() - dateA.getTime();
    });
  } catch (error) {
    console.error('Error getting payroll entries:', error);
    throw error;
  }
}

export async function updatePayrollEntry(
  id: string,
  updates: {
    clock_in?: string | null;
    clock_out?: string | null;
    break_start?: string | null;
    break_end?: string | null;
    hours?: number;
    rate_amount?: number;
    total_minutes?: number;
    total_earnings?: number;
    earnings?: number;
    item_name?: string | null;
    item_description?: string | null;
    notes?: string | null;
    updated_at?: string;
  }
): Promise<any> {
  if (!supabase) throw new Error('Supabase client not initialized');

  console.log('Updating payroll entry:', { id, updates });
  
  try {
    const { data, error } = await supabase
      .from('payroll_page')
      .update(updates)
      .eq('id', id)
      .select(`
        *,
        booking:bookings(
          id,
          business_id,
          booking_number,
          title,
          status,
          created_at,
          end_date,
          end_time,
          completed_at
        ),
        resource:resources(
          first_name,
          last_name,
          email,
          flat_rate_amount,
          hourly_rate,
          compensation_type,
          avatar_url
        ),
        item:flat_rate_items(
          name,
          description
        )
      `)
      .single();

    if (error) {
      console.error('Error updating payroll entry:', error);
      throw error;
    }

    console.log('Payroll entry updated successfully:', data);
    return data;
  } catch (error) {
    console.error('Error updating payroll entry:', error);
    throw error;
  }
}

export async function deletePayrollEntry(id: string): Promise<void> {
  if (!supabase) throw new Error('Supabase client not initialized');

  console.log('Deleting payroll entry:', { id });
  
  try {
    // First, get the payroll entry details to check if it's a flat rate entry
    const { data: payrollEntry, error: fetchError } = await supabase
      .from('payroll_page')
      .select('id, entry_type, flat_rate_id, booking_id, resource_id, completed_at, hours')
      .eq('id', id)
      .single();

    if (fetchError) {
      console.error('Error fetching payroll entry:', fetchError);
      throw fetchError;
    }

    console.log('Found payroll entry:', payrollEntry);

    // If it's a flat rate entry, also delete the corresponding booking_flat_rates entry
    if (payrollEntry.entry_type === 'flat_rate' && payrollEntry.flat_rate_id && payrollEntry.booking_id && payrollEntry.resource_id) {
      console.log('Deleting corresponding booking_flat_rates entry:', {
        booking_id: payrollEntry.booking_id,
        resource_id: payrollEntry.resource_id,
        item_id: payrollEntry.flat_rate_id,
        completed_at: payrollEntry.completed_at
      });

      // First, let's find what booking_flat_rates entries exist for this combination
      const { data: existingEntries, error: findError } = await supabase
        .from('booking_flat_rates')
        .select('*')
        .eq('booking_id', payrollEntry.booking_id)
        .eq('resource_id', payrollEntry.resource_id)
        .eq('item_id', payrollEntry.flat_rate_id);

      if (findError) {
        console.error('Error finding booking_flat_rates entries:', findError);
      } else {
        console.log('Found booking_flat_rates entries:', existingEntries);
      }

      // Simple approach: Delete by booking_id, resource_id, and item_id (flat_rate_id)
      // This should delete the specific flat rate item from the booking
      const { error: flatRateDeleteError, count } = await supabase
        .from('booking_flat_rates')
        .delete()
        .eq('booking_id', payrollEntry.booking_id)
        .eq('resource_id', payrollEntry.resource_id)
        .eq('item_id', payrollEntry.flat_rate_id);

      if (flatRateDeleteError) {
        console.error('Error deleting booking_flat_rates entry:', flatRateDeleteError);
        // Don't throw here - continue to delete payroll entry even if flat rate deletion fails
        console.warn('Continuing with payroll entry deletion despite flat rate deletion error');
      } else {
        console.log('Booking flat rate entry deletion result:', { count, deletedCount: count || 0 });
        if (count && count > 0) {
          console.log('Booking flat rate entry deleted successfully');
        } else {
          console.warn('No booking_flat_rates entries were deleted - they may not exist or not match the criteria');
        }
      }
    }

    // Delete the payroll entry
    const { error: deleteError } = await supabase
      .from('payroll_page')
      .delete()
      .eq('id', id);

    if (deleteError) {
      console.error('Error deleting payroll entry:', deleteError);
      throw deleteError;
    }

    console.log('Payroll entry deleted successfully');
  } catch (error) {
    console.error('Error deleting payroll entry:', error);
    throw error;
  }
}